import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useToggle } from 'Shared/utils/hooks';
import { useCoachingV2RouteHandler } from 'Dashboard/components/CoachingPageV2/use-coaching-v2-route-handler';
import { useReview } from 'Shared/utils/hooks/use-review';

// Types
import { ReviewState } from 'Shared/types/Review.type';
import { Illustrations } from 'Wpds/components/illustration';
import { ReviewTemplateVersion } from 'Shared/types/ReviewTemplates.type';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Utils
import { isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import * as ReviewsUtils from 'Shared/utils/Reviews.utils';

// Selectors
import { getOneOnOneWorkspacesWithBothUsers } from 'Shared/selectors/workspace';

// Helpers
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';

// Actions
import * as reviewActions from 'Shared/actions/Reviews.actions';
import { getWorkspaces } from 'Shared/actions/workspaces';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpText } from 'Wpds/components/text';
import { WpPageLayout, WpPageBody } from 'Wpds/components/page-layout';
import { WpTabs, WpTab } from 'Wpds/components/tabs';
import { WpContainer, WpContainerHeader, WpContainerBody } from 'Wpds/components/container';
import { WpRichTextInput } from 'Wpds/components/rich-text';
import ReviewsPageHeader from '../ThreeSixtyReviewPartials/ReviewsPageHeader/ReviewsPageHeader';
import WpAlert from 'Wpds/components/alert/WpAlert';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import ItemDoesntExistError from 'Shared/components/ItemDoesntExistError/ItemDoesntExistError';
import ThreeSixtySharePageActionItems from './ThreeSixtySharePageActionItems';
import SelfReviewContainer from './SelfReviewContainer';
import SharePageQuestionReview from './SharePageQuestionReview/SharePageQuestionReview';
import EditAndUnshareOptionsMenu from '../ThreeSixtyReviewPartials/EditAndUnshareOptionsMenu/EditAndUnshareOptionsMenu';
import ReviewHistory from '../ThreeSixtyReviewPartials/ReviewHistory/ReviewHistory';
import SharePageReviewForm from './_partials/SharePageReviewForm';
import ThreeSixtySharePageForPrint from './ThreeSixtySharePageForPrint';
import ManagerReviewFormContainer from './ManagerReviewFormContainer/ManagerReviewFormContainer';

// Styles
import './ThreeSixtySharePage.scss';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

enum TabType {
  summary = 'summary',
  self_review = 'self_review',
  peer_reviews = 'peer_reviews',
}

const ThreeSixtySharePage = () => {
  const { handleCoachingV2Redirect } = useCoachingV2RouteHandler();
  const { id: currentUserId, isOrgAdmin } = useCurrentUser();
  const dispatch = useDispatch();
  const history = useHistory();
  const { reviewSlug } = useParams();
  const { featureFlagEnable360PrintButton } = useFlags();
  const [isHistoryDrawerOpen, toggleIsHistoryDrawerOpen] = useToggle(false);
  const [hasError, toggleHasError] = useToggle(false);
  const [isUnlocking, toggleIsUnlocking] = useToggle(false);
  const [activeTab, setActiveTab] = useState(TabType.summary);
  const [showPrinterPage, toggleShowPrinterPage] = useToggle(false);

  const {
    state,
    compilationForm,
    selfPeerReviewForm,
    reviewTemplateVersion,
    isFeedback360,
    isPerformanceReview,
    review,
    reviewJsObject,
    isShared,
    isClosed,
    isCancelled,
    isCompleted,
    receiver,
    compiler,
    creator,
  } = useReview({
    slug: reviewSlug,
  });

  const self360 = ReviewsHelpers.getSelf360(review);
  const isPeerReviewSharedAnonymously = ReviewsUtils.isFeedbackSharedAnonymously({ review });
  const receiverViewedAt = ReviewsHelpers.getReceiverViewedAt(review);

  const receiverName = receiver?.fullName;
  const receiverAvatar = receiver?.avatar;
  const receiverId = receiver?.id;

  const compilerFirstName = compiler?.firstName;
  const compilerId = compiler?.id;

  const creatorId = creator?.id;

  const oneOnOneWorkspaces = useSelector(state => getOneOnOneWorkspacesWithBothUsers(state));
  const oneOnOneWorkspaceForReview = oneOnOneWorkspaces.find(
    ({ users }) => users.find(({ id }) => id === receiverId) && users.find(({ id }) => id === compilerId)
  );

  const selfReivewFormSubmittedAt = ReviewFormHelpers.getSubmittedAt(selfPeerReviewForm);
  const hasSubmittedSelfReviewFoForm = Boolean(selfReivewFormSubmittedAt);

  const introduction = ReviewFormHelpers.getIntroduction(compilationForm);
  const compilationFormSubmitted = !!ReviewFormHelpers.getSubmittedAt(compilationForm);
  const isCompletedButNotShared = compilationFormSubmitted && !isShared;

  const isCurrentUserReceiver = currentUserId === receiverId;
  const isCurrentUserCompiler = currentUserId === compilerId;
  const canViewOnly =
    (!isCurrentUserCompiler && !isCurrentUserReceiver && currentUserId === creatorId) ||
    (isPerformanceReview && isOrgAdmin); // Creator can only view
  const showLockedScreen = isCurrentUserReceiver && !isShared && !isCompleted;
  const showIntroduction = !isEmpty(introduction);

  const pageTitle = (() => {
    if (isPerformanceReview) return `${receiverName} - ${reviewJsObject.reviewCycleName}`;
    if (isCurrentUserReceiver) return 'Your 360 Feedback';
    return `${receiverName}'s 360 Feedback`;
  })();

  useEffect(() => {
    dispatch(getWorkspaces());
  }, []);

  const handleUnlockClick = () => {
    toggleIsUnlocking();
    const data = {
      review: {
        unlock: true,
      },
    };
    dispatch(reviewActions.updateReview({ reviewSlug, data })).then(() => {
      toggleIsUnlocking();
      WpToastManager.success('Review Shared');
    });
  };

  const handleCloseClick = () => {
    if (isPerformanceReview) history.push(PageUrls.PERFORMANCE_REVIEWS_PAST);
    else handleCoachingV2Redirect({ pathname: PageUrls.COACHING_360 });
  };

  useEffect(() => {
    dispatch(reviewActions.fetchReview({ reviewSlug })).then(response => {
      if (response.status === 422 || response.status === 404) toggleHasError();
    });
  }, []);

  useEffect(() => {
    if (isCurrentUserReceiver && !receiverViewedAt && !showLockedScreen)
      dispatch(reviewActions.receiverViewedReview({ reviewSlug }));
  }, [isCurrentUserReceiver, receiverViewedAt]);

  useEffect(() => {
    if (isPerformanceReview && !(showIntroduction || compilationFormSubmitted)) {
      setActiveTab(TabType.peer_reviews);
    }
  }, [isPerformanceReview, showIntroduction, compilationFormSubmitted]);

  const errorJsx = (
    <div style={{ height: '100vh' }}>
      <ItemDoesntExistError
        title="We couldn’t find this review"
        buttonLabel="Back to Coaching"
        buttonCallback={handleCloseClick}
      />
    </div>
  );

  if (hasError) return <>{errorJsx} </>;

  if (!review) return null;

  if (isCancelled || (!isCurrentUserCompiler && !isCurrentUserReceiver && !canViewOnly)) {
    return <>{errorJsx}</>;
  }

  if (
    !isCurrentUserReceiver &&
    !isShared &&
    !isCompleted &&
    !isClosed &&
    ![ReviewState.COMPILATION_COMPLETED_BUT_NOT_SHARED].includes(state)
  ) {
    history.push(PageUrlsUtils.makeReviewCompilationUrl(reviewSlug));
  }

  const tabs = (() => {
    if (self360) return null;

    if (isPerformanceReview && (showIntroduction || compilationFormSubmitted)) {
      return (
        <WpTabs activeTab={activeTab} onTabClick={setActiveTab}>
          <WpTab tabId={TabType.summary}>Summary</WpTab>
          <WpTab tabId={TabType.peer_reviews}>Reviews</WpTab>
        </WpTabs>
      );
    }

    if (!isFeedback360) {
      return null;
    }

    if (isCurrentUserCompiler) {
      return (
        <WpTabs activeTab={activeTab} onTabClick={setActiveTab}>
          <WpTab tabId={TabType.summary}>Summary</WpTab>
          <WpTab tabId={TabType.peer_reviews}>Individual Reviews</WpTab>
        </WpTabs>
      );
    }

    if (isCurrentUserReceiver && isPeerReviewSharedAnonymously) {
      return (
        <WpTabs activeTab={activeTab} onTabClick={setActiveTab}>
          <WpTab tabId={TabType.summary}>Summary</WpTab>
          <WpTab tabId={TabType.peer_reviews}>Individual Reviews</WpTab>
        </WpTabs>
      );
    }

    if (isCurrentUserReceiver && !isPeerReviewSharedAnonymously && selfPeerReviewForm && hasSubmittedSelfReviewFoForm) {
      return (
        <WpTabs activeTab={activeTab} onTabClick={setActiveTab}>
          <WpTab tabId={TabType.summary}>Summary</WpTab>
          <WpTab tabId={TabType.self_review}>Self Review</WpTab>
        </WpTabs>
      );
    }

    return null;
  })();

  const baseClassName = 'ThreeSixtySharePage';
  const classes = classnames(baseClassName, { [`${baseClassName}--print`]: showPrinterPage });

  return (
    <>
      <WpPageLayout width="container" className={classes}>
        <ReviewsPageHeader
          avatar={receiverAvatar}
          title={pageTitle}
          buttons={
            <WpButtonGroup>
              {featureFlagEnable360PrintButton && (
                <WpTooltip>
                  <WpTooltipTrigger>
                    <WpButton onClick={toggleShowPrinterPage} variant="tertiary">
                      <WpIcon name="print" />
                    </WpButton>
                  </WpTooltipTrigger>
                  <WpTooltipContent>Print</WpTooltipContent>
                </WpTooltip>
              )}
              <WpTooltip>
                <WpTooltipTrigger>
                  <WpButton onClick={toggleIsHistoryDrawerOpen} variant="tertiary">
                    <WpIcon name="history" />
                  </WpButton>
                </WpTooltipTrigger>
                <WpTooltipContent>History</WpTooltipContent>
              </WpTooltip>
              {isCurrentUserCompiler && ((isFeedback360 && !self360) || (isPerformanceReview && !isShared)) && (
                <EditAndUnshareOptionsMenu review={review} showAsDropdown={isShared} buttonSize="md" />
              )}
              <WpButton variant="tertiary" onClick={handleCloseClick}>
                <WpIcon name="times" />
                Close
              </WpButton>
            </WpButtonGroup>
          }
          tabs={tabs}
        />
        {showLockedScreen ? (
          <WpPageBody>
            <div className={`${baseClassName}__empty-container`}>
              <EmptyStateContainer
                image={Illustrations.three_people_sitting_under_a_clock_purple_bg}
                title="Your review is locked."
                subTitle={`${compilerFirstName} will unlock it when it’s time to go over it`}
                inContainer
              />
            </div>
          </WpPageBody>
        ) : (
          <WpPageBody>
            <div className={`${baseClassName}__content`}>
              {isCompletedButNotShared && (
                <div className={`${baseClassName}__content__alert`}>
                  <WpAlert>
                    <div className={`${baseClassName}__content__alert__inner`}>
                      {receiverName} can’t see their review
                      {canViewOnly ? ` because ${compilerFirstName} hasn’t unlocked it ` : ''} yet.
                      {!canViewOnly && (
                        <WpButton size="sm" variant="secondary" onClick={handleUnlockClick} loading={isUnlocking}>
                          Share with {receiverName}
                        </WpButton>
                      )}
                    </div>
                  </WpAlert>
                </div>
              )}
              {!self360 && activeTab === TabType.summary && (
                <>
                  <WpText tag="h5" variant="bodyXl" bold>
                    Summary
                  </WpText>
                  {showIntroduction && (
                    <WpContainer className={`${baseClassName}__introduction`}>
                      <WpContainerHeader>Introduction</WpContainerHeader>
                      <WpContainerBody>
                        <WpRichTextInput
                          value={introduction}
                          readOnly
                          options={{ showBorder: false, overflowBehavior: 'grow' }}
                          height="0"
                        />
                      </WpContainerBody>
                    </WpContainer>
                  )}
                  <WpContainer>
                    <WpContainerBody>
                      <SharePageReviewForm review={review} />
                    </WpContainerBody>
                  </WpContainer>
                  {isFeedback360 && oneOnOneWorkspaceForReview && (
                    <WpContainer className="mt-3">
                      <ThreeSixtySharePageActionItems
                        reviewSlug={reviewSlug}
                        workspaceSlug={oneOnOneWorkspaceForReview.id}
                      />
                    </WpContainer>
                  )}
                </>
              )}
              {!self360 &&
                (activeTab === TabType.self_review ||
                  (activeTab === TabType.peer_reviews && reviewTemplateVersion === ReviewTemplateVersion.two)) &&
                selfPeerReviewForm && <SelfReviewContainer review={review} />}
              {isFeedback360 && (self360 || activeTab === TabType.peer_reviews) && (
                <SharePageQuestionReview review={review} />
              )}
              {isPerformanceReview && activeTab === TabType.peer_reviews && (
                <ManagerReviewFormContainer reviewSlug={reviewSlug} />
              )}
            </div>
          </WpPageBody>
        )}
      </WpPageLayout>
      <ReviewHistory review={review} isOpen={isHistoryDrawerOpen} toggleIsOpen={toggleIsHistoryDrawerOpen} />
      <div
        className={`${baseClassName}__print-preview ${showPrinterPage ? `${baseClassName}__print-preview--show` : ''}`}
      >
        <ThreeSixtySharePageForPrint review={review} togglePrintPreview={toggleShowPrinterPage} />
      </div>
    </>
  );
};

export default ThreeSixtySharePage;
